import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


const emptyLine = () => ({ accountId: '', description: '', debit: '', credit: '' });

const balanceHint = (type, side) => {
  if (!type) return null;
  const increases = ['ASSET', 'EXPENSE'].includes(type) ? 'debit' : 'credit';
  const label = side === increases ? '↑ Increases' : '↓ Decreases';
  const color = side === increases ? 'text-green-600' : 'text-red-500';
  return <span className={`block text-xs ${color} mt-0.5`}>{label}</span>;
};

export default function AddJournalEntry() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ entryDate: new Date().toISOString().split('T')[0], reference: '', description: '', status: 'DRAFT' });
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  useEffect(() => {
    api(`/accounts`).then(r => r.json()).then(setAccounts).catch(() => {});
  }, []);

  const updateLine = (i, field, value) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: value };
    setLines(updated);
  };

  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      lines: lines.filter(l => l.accountId).map(l => ({
        accountId: l.accountId,
        description: l.description,
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0,
      })),
    };
    const res = await api(`/journal-entries`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const je = await res.json();
      navigate(`/accounting/journal-entries/${je.id}`);
    }
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">New Journal Entry</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" required value={form.entryDate} onChange={(e) => setForm({...form, entryDate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
              <input type="text" value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600">
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Lines</h2>
              <button type="button" onClick={addLine} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">+ Add Line</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2">
                        <select value={line.accountId} onChange={(e) => updateLine(i, 'accountId', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm">
                          <option value="">Select account</option>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input type="text" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" step="0.01" min="0" value={line.debit} onChange={(e) => updateLine(i, 'debit', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm" />
                        {balanceHint(accounts.find(a => a.id === line.accountId)?.type, 'debit')}
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" step="0.01" min="0" value={line.credit} onChange={(e) => updateLine(i, 'credit', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm" />
                        {balanceHint(accounts.find(a => a.id === line.accountId)?.type, 'credit')}
                      </td>
                      <td className="px-4 py-2">
                        {lines.length > 2 && <button type="button" onClick={() => removeLine(i)} className="text-red-500 hover:text-red-700 text-sm">✕</button>}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={2} className="px-4 py-2 text-right text-sm">Totals</td>
                    <td className="px-4 py-2 text-right text-sm">{totalDebit.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right text-sm">{totalCredit.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!balanced && totalDebit + totalCredit > 0 && (
              <p className="text-red-600 text-sm mt-2">Debits and credits must be equal. Difference: {Math.abs(totalDebit - totalCredit).toFixed(2)}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => navigate('/accounting/journal-entries')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={!balanced} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">Save</button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
